import React, { useState } from 'react';
import {
  Brain,
  Bot,
  Cpu,
  Database,
  Zap,
  ShieldCheck,
  Workflow,
  Mic,
  Eye,
  CheckCircle2,
  ArrowRight,
  Activity,
  Terminal,
  TrendingUp,
  LineChart
} from 'lucide-react';
import { Button } from '../components/ui/button';

const AIMLSolutions = () => {
  const [activeModel, setActiveModel] = useState('gpt4');

  const aiPillars = [
    {
      icon: Database,
      title: 'Enterprise RAG & Vector Search',
      badge: 'Zero Hallucination',
      color: 'from-cyan-500 to-blue-600',
      description: 'Retrieval-Augmented Generation pipelines using Pinecone, ChromaDB, and Milvus to query internal PDF manuals, SQL databases, and Notion docs with grounded accuracy.',
      bullets: ['Pinecone & Milvus Vector Indexing', 'Semantic Chunking & Re-Ranking', 'Grounded Source Citation']
    },
    {
      icon: Workflow,
      title: 'Autonomous AI Agents & Workflows',
      badge: 'Multi-Step Execution',
      color: 'from-purple-500 to-indigo-600',
      description: 'LangChain and AutoGen multi-agent systems capable of reasoning, calling APIs, querying databases, and executing complex workflows without human intervention.',
      bullets: ['LangChain & AutoGen Orchestration', 'Automated CRM & Webhook Actions', 'Multi-Agent Tool Binding']
    },
    {
      icon: Cpu,
      title: 'Custom LLM Fine-Tuning & Private Models',
      badge: 'Data Sovereignty',
      color: 'from-orange-500 to-amber-500',
      description: 'Fine-tuning open-source models (Llama 3, Mistral, Qwen) using LoRA / QLoRA techniques on self-hosted cloud GPUs for 100% data privacy and domain expertise.',
      bullets: ['Llama 3 & Mistral Fine-Tuning', 'Self-Hosted On-Premise Cloud GPUs', 'LoRA & QLoRA Optimization']
    },
    {
      icon: TrendingUp,
      title: 'Predictive Analytics & ML Forecasting',
      badge: 'Data Intelligence',
      color: 'from-emerald-500 to-teal-600',
      description: 'Time-series forecasting, customer churn prediction, and automated anomaly detection engines powered by Scikit-Learn, PyTorch, and XGBoost.',
      bullets: ['Time-Series Demand Forecasting', 'Customer Churn & LTV Modeling', 'Real-Time Fraud Anomaly Alert']
    },
    {
      icon: Mic,
      title: 'Real-Time Conversational Voice AI',
      badge: '< 300ms Latency',
      color: 'from-pink-500 to-rose-600',
      description: 'Ultra-low latency human-like voice agents (Whisper + ElevenLabs) capable of handling inbound customer calls, booking appointments, and outbound phone campaigns.',
      bullets: ['Whisper Speech-to-Text Stream', 'ElevenLabs Humanlike Voice Synth', 'Twilio Telephony & SIP Trunk Sync']
    },
    {
      icon: Eye,
      title: 'Multi-Modal Vision & Document AI',
      badge: 'OCR & Analysis',
      color: 'from-blue-500 to-cyan-600',
      description: 'Extract structured data from scanned invoices, receipts, blueprints, and medical reports using multi-modal GPT-4 Vision models.',
      bullets: ['Invoice & Document Parsing', 'Visual Quality Control Inspection', 'Automated Data Structuring']
    }
  ];

  const modelSpecs = {
    gpt4: {
      name: 'OpenAI GPT-4o & O1 Ecosystem',
      desc: 'Top-tier general intelligence, advanced multi-modal capabilities, and complex reasoning for enterprise decision support.',
      specs: ['128k Context Window', 'JSON Mode Structured Output', 'Multi-Modal Vision Processing', 'Built-in Code Execution']
    },
    claude: {
      name: 'Anthropic Claude 3.5 Sonnet',
      desc: 'Industry-leading coding abilities, nuanced writing, and superior performance for long-context document analysis.',
      specs: ['200k Context Window', 'Artifacts Workspace Generation', 'Superior Code Refactoring', 'Strict AI Safety Alignment']
    },
    llama: {
      name: 'Open-Source Llama 3 (Self-Hosted)',
      desc: '100% data privacy and zero vendor lock-in. Self-hosted on your private cloud infrastructure for strict compliance.',
      specs: ['100% Data Sovereignty', 'Zero Third-Party Data Sharing', 'Fine-Tuned Domain Knowledge', 'Cost-Effective Token Ingestion']
    }
  };

  const aiLifecycle = [
    { step: '01', title: 'Data Ingestion & Vector Embedding', desc: 'Connecting raw documents, database tables, and APIs into Pinecone / Milvus vector stores.' },
    { step: '02', title: 'RAG Pipeline & Guardrails Configuration', desc: 'Setting up semantic search chunking, re-ranking, and strict PII / safety guardrails.' },
    { step: '03', title: 'Multi-Agent Tool & API Binding', desc: 'Wiring LLM agents with custom webhooks, database write tools, and CRM actions.' },
    { step: '04', title: 'Evaluation & Human-in-the-Loop Benchmarking', desc: 'Testing response groundedness, latency benchmarks, and edge-case accuracy.' },
    { step: '05', title: 'Production Deployment & Continuous Telemetry', desc: 'Deploying across Web, WhatsApp, and Slack with real-time token telemetry.' }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-left pt-20 text-gray-100">

      {/* Hero Showcase Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 via-slate-950 to-gray-950 relative overflow-hidden border-b border-gray-800/80">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Left Copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-6">
                <Brain className="w-4 h-4 text-orange-400" />
                <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                  Enterprise AI & Neural Solutions
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                Intelligent AI & ML Solutions, <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Autonomous Agents</span> & Predictive Models
              </h1>

              <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-8">
                Automate complex workflows, query company knowledge bases with zero hallucination RAG, and deploy 24/7 conversational voice and chat AI agents fine-tuned on your data.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 flex items-center gap-2">
                    Build Custom AI Solution
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="#ai-capabilities">
                  <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 px-8 py-6 text-base font-semibold rounded-xl">
                    Explore AI Pillars
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Live AI Telemetry Console Graphic Box */}
            <div className="lg:col-span-5">
              <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-6 shadow-2xl relative overflow-hidden backdrop-blur-xl">

                {/* Console Header */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-800 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-cyan-400 animate-ping" />
                    <span className="text-sm font-mono text-cyan-400 font-bold">NEURAL ENGINE: ACTIVE</span>
                  </div>
                  <span className="text-[11px] font-mono text-gray-400">GPT-4o / RAG v3.2</span>
                </div>

                {/* Telemetry Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">First Token Latency</div>
                    <div className="text-2xl font-extrabold text-cyan-400 font-mono">&lt; 118 ms</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">RAG Groundedness</div>
                    <div className="text-2xl font-extrabold text-orange-400 font-mono">99.8%</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Vector DB Index</div>
                    <div className="text-2xl font-extrabold text-emerald-400 font-mono">PINECONE</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Generation Speed</div>
                    <div className="text-2xl font-extrabold text-blue-400 font-mono">145 Tok/s</div>
                  </div>
                </div>

                {/* Terminal Line */}
                <div className="bg-gray-950 p-3 rounded-xl border border-gray-800 font-mono text-sm text-gray-400 flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-orange-400" />
                  <span>aiml_engine.predict("vector search success")</span>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* AI Performance Metrics Banner */}
      <section className="py-10 bg-gray-900/80 border-y border-gray-800/80">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-cyan-400 mb-1">&lt; 120ms</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">First Token Latency</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-orange-400 mb-1">99.8%</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">RAG Grounded Accuracy</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-blue-400 mb-1">70%+</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Support Cost Savings</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-emerald-400 mb-1">24/7</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Omnichannel Telemetry</div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Pillars Grid */}
      <section id="ai-capabilities" className="py-24 bg-gray-950">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                Enterprise AI Capability Grid
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              End-to-End <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">AI & ML Engineering</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              From custom RAG vector search pipelines to autonomous LangChain agents, predictive forecasting, and voice AI bots.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aiPillars.map((pillar, idx) => (
              <div
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 hover:border-orange-500/70 rounded-2xl p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-orange-500/10 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${pillar.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                      <pillar.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-1 rounded-full">
                      {pillar.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-amber-300 transition-colors">
                    {pillar.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-800/80 space-y-2">
                  {pillar.bullets.map((bullet, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-300 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 text-orange-400 mr-2 flex-shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Interactive LLM Model Switcher */}
      <section className="py-20 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-white mb-4">
              Supported Foundation AI Models
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto mb-8">
              We integrate proprietary foundation models or train self-hosted open-source models on your infrastructure.
            </p>

            <div className="inline-flex p-1 bg-gray-900 border border-gray-800 rounded-xl space-x-2">
              <button
                onClick={() => setActiveModel('gpt4')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModel === 'gpt4'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                OpenAI GPT-4o
              </button>
              <button
                onClick={() => setActiveModel('claude')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModel === 'claude'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Claude 3.5 Sonnet
              </button>
              <button
                onClick={() => setActiveModel('llama')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModel === 'llama'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Llama 3 (Self-Hosted)
              </button>
            </div>
          </div>

          <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-3">
              {modelSpecs[activeModel].name}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-8">
              {modelSpecs[activeModel].desc}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {modelSpecs[activeModel].specs.map((spec, idx) => (
                <div key={idx} className="bg-gray-950/80 border border-gray-800 rounded-xl p-4 flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-200">{spec}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 5-Step AI Implementation Lifecycle */}
      <section className="py-24 bg-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
              5-Step AI Agent Engineering Lifecycle
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              From raw data vector indexing to production guardrails and continuous telemetry.
            </p>
          </div>

          <div className="space-y-6">
            {aiLifecycle.map((proc, idx) => (
              <div key={idx} className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6 flex flex-col md:flex-row items-start md:items-center gap-6 hover:border-orange-500/50 transition-all">
                <span className="text-3xl font-black text-orange-400 font-mono flex-shrink-0">{proc.step}</span>
                <div>
                  <h4 className="text-lg font-bold text-white mb-1">{proc.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">{proc.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Full-Width Executive AI Consultation CTA Section */}
      <section className="py-24 bg-gradient-to-r from-slate-900 via-gray-950 to-slate-900 relative overflow-hidden border-t border-gray-800/80 w-full">
        <div className="container mx-auto px-6 relative z-10 max-w-4xl text-center">
          <div className="bg-gradient-to-r from-slate-900 via-gray-800 to-slate-900 border border-orange-500/30 rounded-3xl p-10 md:p-14 shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Ready to Deploy Custom AI & ML Solutions?
            </h2>
            <p className="text-gray-300 text-base mb-8 max-w-2xl mx-auto">
              Schedule a technical session with our AI engineers to index your enterprise data, build RAG vector pipelines, and test live AI prototypes.
            </p>
            <a href="/contact">
              <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg">
                Schedule AI Architecture Consultation
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AIMLSolutions;